// Kept in step with the canonical trusted-domain list in auth/middleware.ts
// (`ALLOWED_DOMAINS`), which includes the EU estate — a returnUrl on a
// `.pypestream.eu` host (EU instances) must not be rejected and sent to home.
const ALLOWED_HOST_SUFFIXES = [
  '.pypestream.com',
  '.pypestream.dev',
  '.pypestream.eu',
];
const ALLOWED_APEX_HOSTS = ['pypestream.com'];

// Whitespace, C0/C1 control characters and backslashes. Browsers strip or
// rewrite some of these while parsing a URL (tab and newline vanish, `\` turns
// into `/`), so a value carrying one can mean something other than what a
// string check saw. Refusing them outright is simpler than modelling that.
// eslint-disable-next-line no-control-regex
const FORBIDDEN_CHARS = /[\u0000- \u007f-\u009f\\]/;

const isAllowedHostname = (hostname: string): boolean =>
  ALLOWED_APEX_HOSTS.includes(hostname) ||
  ALLOWED_HOST_SUFFIXES.some((suffix) => hostname.endsWith(suffix));

/**
 * Decide whether a "return to" URL taken from the query string may be used as a
 * redirect target after access has been granted. Returns the URL to navigate to,
 * or `null` when it must be ignored (open-redirect guard).
 *
 * Allowed: a relative path starting with exactly one `/`, or an absolute
 * http(s) URL on the current origin or on pypestream.com / *.pypestream.com /
 * *.pypestream.dev. Absolute URLs carrying userinfo are refused, since
 * `https://pypestream.com@evil.com` reads as the allowed host to a human.
 */
export const resolveAccessReturnUrl = (
  raw: string | null | undefined,
  currentOrigin: string
): string | null => {
  if (typeof raw !== 'string' || raw === '' || FORBIDDEN_CHARS.test(raw)) {
    return null;
  }

  if (raw.startsWith('/')) {
    // `//host` is protocol-relative, i.e. a different site.
    if (raw.startsWith('//')) {
      return null;
    }
    // Resolve against the current origin and re-check it, rather than trusting
    // the raw string: a path that normalises onto another origin, or back to a
    // protocol-relative `//`, is refused. Defense in depth over FORBIDDEN_CHARS,
    // which already blocks the backslash a browser would rewrite into `//`.
    let resolved: URL;
    try {
      resolved = new URL(raw, currentOrigin);
    } catch {
      return null;
    }
    if (resolved.origin !== currentOrigin) {
      return null;
    }
    const normalized = `${resolved.pathname}${resolved.search}${resolved.hash}`;
    return normalized.startsWith('//') ? null : normalized;
  }

  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }

  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    return null;
  }
  if (url.username !== '' || url.password !== '') {
    return null;
  }

  return url.origin === currentOrigin || isAllowedHostname(url.hostname)
    ? url.href
    : null;
};
